// APExplorer.jsx
import { useState, useEffect, useRef } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../config/firebase";

// ─── Constants ────────────────────────────────────────────────────────────────
const ALL_YEARS = ["2018", "2019", "2020", "2021", "2022", "2023", "2024", "2025", "2026"];

const DISCIPLINE_COLORS = {
  "Mathematics and Computer Science": "#1B3A6B",
  "History and Social Sciences":      "#BA7517",
  "Sciences":                         "#1D9E75",
  "English":                          "#7B4EA0",
  "Arts":                             "#C0392B",
  "World Languages":                  "#2471A3",
  "AP Capstone":                      "#888",
};

const GRADE_COLORS = {
  "10th Grade": "#1B3A6B",
  "11th Grade": "#BA7517",
  "12th Grade": "#1D9E75",
};

const SCORE_COLORS = {
  1: "#E24B4A", 2: "#e87a3a", 3: "#BA7517", 4: "#6aab8e", 5: "#1D9E75",
};

const TABS = [
  { key: "overview", label: "Overview"        },
  { key: "trends",   label: "Trends Over Time" },
  { key: "bygrade",  label: "Score by Grade"   },
];

// ─── Firestore hooks ──────────────────────────────────────────────────────────
const dataCache = {};

function useAPData(year) {
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!year) return;
    if (dataCache[year]) { setData(dataCache[year]); setLoading(false); return; }
    setLoading(true); setError(null);
    getDocs(query(collection(db, "apScores"), where("year", "==", year)))
      .then(snap => {
        const rows = snap.docs.map(d => d.data());
        dataCache[year] = rows;
        setData(rows); setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [year]);

  return { data, loading, error };
}

function useAllYearsData(enabled) {
  const [allData, setAllData] = useState({});
  const [loading, setLoading] = useState(false);
  const fetched = useRef(false);

  useEffect(() => {
    if (!enabled || fetched.current) return;
    fetched.current = true;
    setLoading(true);
    Promise.all(
      ALL_YEARS.map(yr =>
        (dataCache[yr]
          ? Promise.resolve([yr, dataCache[yr]])
          : getDocs(query(collection(db, "apScores"), where("year", "==", yr)))
              .then(snap => { const r = snap.docs.map(d => d.data()); dataCache[yr] = r; return [yr, r]; })
        )
      )
    ).then(results => {
      const map = {};
      results.forEach(([yr, rows]) => { map[yr] = rows; });
      setAllData(map); setLoading(false);
    });
  }, [enabled]);

  return { allData, loading };
}

// ─── Stat helpers ─────────────────────────────────────────────────────────────
function computeStats(rows) {
  if (!rows || !rows.length) return { mean: null, passRate: null, total: 0, dist: {1:0,2:0,3:0,4:0,5:0} };
  const dist = {1:0,2:0,3:0,4:0,5:0};
  rows.forEach(r => { if (dist[r.score] !== undefined) dist[r.score]++; });
  const total    = rows.length;
  const mean     = rows.reduce((s, r) => s + r.score, 0) / total;
  const passing  = dist[3] + dist[4] + dist[5];
  const passRate = (passing / total) * 100;
  return { mean, passRate, total, dist };
}

function normalizeSubject(subject) {
  return (subject || "")
    .replace(/^AP\s+/i, "")
    .replace(/\s*-\s*U$/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function groupBy(rows, key) {
  return rows.reduce((acc, r) => {
    const k = r[key]; if (!acc[k]) acc[k] = []; acc[k].push(r); return acc;
  }, {});
}

function trendArrow(first, last) {
  if (first == null || last == null) return null;
  const delta = last - first;
  const pct = Math.abs(delta);
  const color = delta > 0 ? "#1D9E75" : delta < 0 ? "#E24B4A" : "#bbb";
  const arrow = delta > 0 ? "↑" : delta < 0 ? "↓" : "→";
  return { arrow, delta, pct, color };
}

// ─── Shared atoms ─────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, accent, trend }) {
  return (
    <div style={{ background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, padding:"14px 16px", borderLeft: accent ? `3px solid ${accent}` : undefined }}>
      <div style={{ fontSize:10, color:"#999", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:6 }}>{label}</div>
      <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
        <div style={{ fontSize:22, fontWeight:400, color: accent||"#1a1a18" }}>{value}</div>
        {trend && (
          <span style={{ fontSize:12, color:trend.color, fontFamily:"monospace", fontWeight:600 }}>
            {trend.arrow} {trend.pct.toFixed(1)}{typeof trend.delta === "number" && trend.delta % 1 !== 0 ? "" : ""}
          </span>
        )}
      </div>
      {sub && <div style={{ fontSize:10, color:"#aaa", fontFamily:"monospace", marginTop:4 }}>{sub}</div>}
    </div>
  );
}

function PassBadge({ rate }) {
  if (rate === null || rate === undefined) return <span style={{ color:"#ddd", fontFamily:"monospace", fontSize:11 }}>—</span>;
  const color = rate >= 90 ? "#1D9E75" : rate >= 70 ? "#BA7517" : "#E24B4A";
  return <span style={{ fontSize:11, padding:"2px 7px", borderRadius:4, background:color+"18", color, fontFamily:"monospace", fontWeight:600 }}>{rate.toFixed(1)}%</span>;
}

function MiniBar({ dist }) {
  const total = Object.values(dist).reduce((a,b) => a+b, 0);
  if (!total) return <span style={{ color:"#ddd", fontFamily:"monospace", fontSize:11 }}>—</span>;
  return (
    <div style={{ display:"flex", height:12, borderRadius:3, overflow:"hidden", width:100, gap:1 }}>
      {[1,2,3,4,5].map(s => {
        const w = (dist[s]/total)*100;
        return w > 0 ? <div key={s} title={`Score ${s}: ${dist[s]}`} style={{ width:`${w}%`, background:SCORE_COLORS[s] }} /> : null;
      })}
    </div>
  );
}

function LoadingState() {
  return <div style={{ textAlign:"center", padding:"4rem", color:"#bbb", fontFamily:"monospace", fontSize:12, letterSpacing:"0.06em" }}>loading data…</div>;
}

function EmptyState({ year }) {
  return <div style={{ textAlign:"center", padding:"4rem", color:"#bbb", fontFamily:"monospace", fontSize:12 }}>No data found for {year}.</div>;
}

function YearPill({ value, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      fontSize:11, padding:"4px 11px", borderRadius:4, border:"0.5px solid",
      borderColor: active ? "#1a1a18" : "#d4d3cc",
      background: active ? "#1a1a18" : "transparent",
      color: active ? "#fff" : "#888",
      cursor:"pointer", fontFamily:"monospace", transition:"all 0.15s",
    }}>{value}</button>
  );
}

// ─── Aggregate trend chart ────────────────────────────────────────────────────
function AggregateTrendsChart({ allData }) {
  const [hoveredYr, setHoveredYr] = useState(null);
  const svgRef = useRef(null);

  // Build year-by-year aggregate stats
  const yearStats = ALL_YEARS.map(yr => {
    const rows = allData[yr] || [];
    if (!rows.length) return { year: yr, mean: null, passRate: null, total: null };
    const s = computeStats(rows);
    return { year: yr, mean: s.mean, passRate: s.passRate, total: s.total };
  }).filter(d => d.mean !== null);

  if (yearStats.length < 2) return null;

  const firstYr  = yearStats[0];
  const lastYr   = yearStats[yearStats.length - 1];
  const meanTrend = trendArrow(firstYr.mean, lastYr.mean);
  const passTrend = trendArrow(firstYr.passRate, lastYr.passRate);
  const volTrend  = trendArrow(firstYr.total, lastYr.total);

  // Chart
  const W=700, H=260, PAD={top:24,right:24,bottom:40,left:52};
  const cW=W-PAD.left-PAD.right, cH=H-PAD.top-PAD.bottom;

  const xPos = ALL_YEARS.reduce((acc,yr,i) => {
    acc[yr] = (i/(ALL_YEARS.length-1))*cW; return acc;
  }, {});

  // Two y-axes: left=mean (1–5), right=pass rate (0–100)
  const meanMin = 1, meanMax = 5;
  const passMin = 0, passMax = 100;

  function yMean(v)  { return cH - ((v-meanMin)/(meanMax-meanMin))*cH; }
  function yPass(v)  { return cH - ((v-passMin)/(passMax-passMin))*cH; }

  const meanPath = yearStats.map((d,i) => `${i===0?"M":"L"} ${xPos[d.year]} ${yMean(d.mean)}`).join(" ");
  const passPath = yearStats.map((d,i) => `${i===0?"M":"L"} ${xPos[d.year]} ${yPass(d.passRate)}`).join(" ");

  const hoveredStat = hoveredYr ? yearStats.find(d=>d.year===hoveredYr) : null;

  function handleMove(e) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left - PAD.left;
    const nearest = ALL_YEARS.reduce((b,yr) =>
      Math.abs(xPos[yr]-mx) < Math.abs(xPos[b]-mx) ? yr : b, ALL_YEARS[0]);
    setHoveredYr(nearest);
  }

  return (
    <div style={{ marginBottom:"2rem" }}>
      {/* Section label */}
      <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:10 }}>
        program-wide · all subjects · {firstYr.year}–{lastYr.year}
      </div>

      {/* Trend summary cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:"1.25rem" }}>
        <StatCard
          label="Overall Mean Score"
          value={lastYr.mean.toFixed(2)}
          sub={`${firstYr.year}: ${firstYr.mean.toFixed(2)} → ${lastYr.year}: ${lastYr.mean.toFixed(2)}`}
          accent="#1B3A6B"
          trend={{ ...meanTrend, pct: Math.abs(lastYr.mean - firstYr.mean) }}
        />
        <StatCard
          label="Overall Pass Rate"
          value={lastYr.passRate.toFixed(1)+"%"}
          sub={`${firstYr.year}: ${firstYr.passRate.toFixed(1)}% → ${lastYr.year}: ${lastYr.passRate.toFixed(1)}%`}
          accent="#1D9E75"
          trend={{ ...passTrend, pct: Math.abs(lastYr.passRate - firstYr.passRate) }}
        />
        <StatCard
          label="Total Exams"
          value={lastYr.total.toLocaleString()}
          sub={`${firstYr.year}: ${firstYr.total} → ${lastYr.year}: ${lastYr.total}`}
          accent="#BA7517"
          trend={{ ...volTrend, pct: Math.abs(lastYr.total - firstYr.total) }}
        />
      </div>

      {/* Dual-line chart */}
      <div style={{ position:"relative", background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, padding:"1rem" }}>
        <div style={{ display:"flex", gap:20, marginBottom:8 }}>
          <span style={{ display:"flex", alignItems:"center", gap:6, fontSize:10, color:"#aaa", fontFamily:"monospace" }}>
            <span style={{ width:20, height:2, background:"#1B3A6B", display:"inline-block", borderRadius:1 }}/>mean score (left axis)
          </span>
          <span style={{ display:"flex", alignItems:"center", gap:6, fontSize:10, color:"#aaa", fontFamily:"monospace" }}>
            <span style={{ width:20, height:2, background:"#1D9E75", display:"inline-block", borderRadius:1, borderTop:"2px dashed #1D9E75", height:0 }}/>
            <span style={{ width:20, height:0, borderTop:"2px dashed #1D9E75", display:"inline-block" }}/>pass rate % (right axis)
          </span>
        </div>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", display:"block", cursor:"crosshair" }}
          onMouseMove={handleMove} onMouseLeave={()=>setHoveredYr(null)}>
          <g transform={`translate(${PAD.left},${PAD.top})`}>
            {/* Grid */}
            {[1,2,3,4,5].map(t=>(
              <line key={t} x1={0} x2={cW} y1={yMean(t)} y2={yMean(t)} stroke="#f5f4f0" strokeWidth={0.5}/>
            ))}
            {ALL_YEARS.map(yr=>(
              <line key={yr} x1={xPos[yr]} x2={xPos[yr]} y1={0} y2={cH} stroke="#f5f4f0" strokeWidth={0.5}/>
            ))}

            {/* Axes */}
            <line x1={0}  x2={0}  y1={0}  y2={cH}  stroke="#d4d3cc" strokeWidth={0.5}/>
            <line x1={cW} x2={cW} y1={0}  y2={cH}  stroke="#d4d3cc" strokeWidth={0.5}/>
            <line x1={0}  x2={cW} y1={cH} y2={cH}  stroke="#d4d3cc" strokeWidth={0.5}/>

            {/* Left axis: mean score */}
            {[1,2,3,4,5].map(t=>(
              <text key={t} x={-8} y={yMean(t)+3} textAnchor="end" fontSize={9} fill="#1B3A6B" fontFamily="monospace" opacity={0.6}>{t}</text>
            ))}
            <text x={-cH/2} y={-40} textAnchor="middle" fontSize={9} fill="#1B3A6B" fontFamily="monospace" opacity={0.6} transform="rotate(-90)">mean score</text>

            {/* Right axis: pass rate */}
            {[0,25,50,75,100].map(t=>(
              <text key={t} x={cW+8} y={yPass(t)+3} textAnchor="start" fontSize={9} fill="#1D9E75" fontFamily="monospace" opacity={0.6}>{t}%</text>
            ))}

            {/* Year labels */}
            {ALL_YEARS.map(yr=>(
              <text key={yr} x={xPos[yr]} y={cH+14} textAnchor="middle" fontSize={9} fill="#ccc" fontFamily="monospace">{yr}</text>
            ))}

            {/* Pass rate line (dashed, green) */}
            <path d={passPath} fill="none" stroke="#1D9E75" strokeWidth={1.5} strokeDasharray="5,3"/>
            {yearStats.map(d=>(
              <circle key={d.year} cx={xPos[d.year]} cy={yPass(d.passRate)} r={hoveredYr===d.year?5:3}
                fill="#1D9E75" opacity={0.85} style={{transition:"r 0.1s"}}/>
            ))}

            {/* Mean score line (solid, navy) */}
            <path d={meanPath} fill="none" stroke="#1B3A6B" strokeWidth={2}/>
            {yearStats.map(d=>(
              <circle key={d.year} cx={xPos[d.year]} cy={yMean(d.mean)} r={hoveredYr===d.year?5:3}
                fill="#1B3A6B" style={{transition:"r 0.1s"}}/>
            ))}

            {/* Hover vertical line */}
            {hoveredYr && xPos[hoveredYr] != null && (
              <line x1={xPos[hoveredYr]} x2={xPos[hoveredYr]} y1={0} y2={cH}
                stroke="#1a1a18" strokeWidth={0.5} strokeDasharray="3,2" opacity={0.3}/>
            )}
          </g>
        </svg>

        {/* Hover tooltip */}
        {hoveredYr && hoveredStat && (
          <div style={{ position:"absolute", left:16, top:16, background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, padding:"10px 14px", fontSize:11, pointerEvents:"none", zIndex:10, minWidth:160, boxShadow:"0 2px 8px rgba(0,0,0,0.08)", fontFamily:"monospace" }}>
            <div style={{ fontWeight:600, marginBottom:6, fontSize:13, color:"#1a1a18", fontFamily:"'Georgia',serif" }}>{hoveredYr}</div>
            <div style={{ color:"#1B3A6B", marginBottom:3 }}>Mean: <strong>{hoveredStat.mean.toFixed(2)}</strong></div>
            <div style={{ color:"#1D9E75", marginBottom:3 }}>Pass Rate: <strong>{hoveredStat.passRate.toFixed(1)}%</strong></div>
            <div style={{ color:"#BA7517" }}>Exams: <strong>{hoveredStat.total.toLocaleString()}</strong></div>
          </div>
        )}
      </div>

      {/* Year-by-year aggregate table */}
      <div style={{ background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, overflow:"hidden", marginTop:"1rem" }}>
        <div style={{ padding:"10px 16px", borderBottom:"0.5px solid #e8e7e0", fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace" }}>
          year-by-year summary · all subjects combined
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
            <thead>
              <tr style={{ background:"#FAFAF8" }}>
                <th style={{ padding:"7px 14px", textAlign:"left", fontWeight:400, color:"#bbb", borderBottom:"0.5px solid #e8e7e0", fontFamily:"monospace", fontSize:10 }}>Metric</th>
                {yearStats.map(d=>(
                  <th key={d.year} style={{ padding:"7px 10px", textAlign:"center", fontWeight:400, color:"#bbb", borderBottom:"0.5px solid #e8e7e0", fontFamily:"monospace", fontSize:10 }}>{d.year}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label:"Mean Score",  key:"mean",     fmt: v=>v.toFixed(2),    color:"#1B3A6B" },
                { label:"Pass Rate",   key:"passRate", fmt: v=>v.toFixed(1)+"%", color:"#1D9E75" },
                { label:"Total Exams", key:"total",    fmt: v=>v.toLocaleString(), color:"#BA7517" },
              ].map((row,ri)=>(
                <tr key={row.key} style={{ background:ri%2===0?"#fff":"#FAFAF8", borderBottom:"0.5px solid #f0efe8" }}>
                  <td style={{ padding:"8px 14px", color:row.color, fontWeight:600, fontFamily:"monospace", fontSize:11 }}>{row.label}</td>
                  {yearStats.map((d,i)=>{
                    const v = d[row.key];
                    const prev = i > 0 ? yearStats[i-1][row.key] : null;
                    const delta = prev != null ? v - prev : null;
                    const dColor = delta > 0 ? "#1D9E75" : delta < 0 ? "#E24B4A" : "#bbb";
                    return (
                      <td key={d.year} style={{ padding:"8px 10px", textAlign:"center", fontFamily:"monospace", color:"#444", fontSize:11 }}>
                        <div>{row.fmt(v)}</div>
                        {delta != null && (
                          <div style={{ fontSize:9, color:dColor }}>
                            {delta > 0 ? "+" : ""}{row.key==="total" ? Math.round(delta) : row.key==="passRate" ? delta.toFixed(1)+"%" : delta.toFixed(2)}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Tab 1: Overview ──────────────────────────────────────────────────────────
function OverviewTab({ data, loading, error, year }) {
  const [disciplineFilter, setDisciplineFilter] = useState("all");
  const [sortBy, setSortBy]                     = useState("subject");
  const [hoveredSubject, setHoveredSubject]      = useState(null);
  const [tooltip, setTooltip]                   = useState({ visible:false, x:0, y:0, subject:null });
  const svgRef = useRef(null);

  if (loading) return <LoadingState />;
  if (error)   return <div style={{ padding:"3rem", color:"#E24B4A", fontFamily:"monospace", fontSize:12 }}>Error: {error}</div>;
  if (!data.length) return <EmptyState year={year} />;

  const normalizedData = data.map(r => ({ ...r, subject: normalizeSubject(r.subject) }));
  const overall      = computeStats(normalizedData);
  const bySubject    = groupBy(normalizedData, "subject");
  const byDiscipline = groupBy(normalizedData, "discipline");
  const disciplines  = Object.keys(byDiscipline).sort();

  const subjectRows = Object.entries(bySubject).map(([subject, rows]) => {
    const stats = computeStats(rows);
    return { subject, discipline: rows[0]?.discipline || "", ...stats };
  })
  .filter(r => disciplineFilter === "all" || r.discipline === disciplineFilter)
  .sort((a,b) => {
    if (sortBy === "mean")  return (b.mean||0)-(a.mean||0);
    if (sortBy === "pass")  return (b.passRate||0)-(a.passRate||0);
    if (sortBy === "total") return b.total-a.total;
    return a.subject.localeCompare(b.subject);
  });

  const scatter = subjectRows.filter(r => r.mean !== null && r.passRate !== null);
  const SW=580, SH=290, SP={top:16,right:20,bottom:44,left:46};
  const scW=SW-SP.left-SP.right, scH=SH-SP.top-SP.bottom;
  function sx(v){ return (v/100)*scW; }
  function sy(v){ return scH-((v-1)/4)*scH; }

  function handleScatterMove(e, d) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    setTooltip({ visible:true, x:e.clientX-rect.left, y:e.clientY-rect.top, subject:d });
  }

  return (
    <>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:"1.5rem" }}>
        <StatCard label="Total Exams"      value={overall.total.toLocaleString()} />
        <StatCard label="Mean Score"       value={overall.mean?.toFixed(2)??"—"} />
        <StatCard label="Pass Rate (3+)"   value={overall.passRate?.toFixed(1)+"%"} accent="#1D9E75" />
        <StatCard label="Subjects Offered" value={Object.keys(bySubject).length} />
      </div>

      <div style={{ marginBottom:"1.5rem" }}>
        <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:8 }}>by discipline · click to filter</div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(190px,1fr))", gap:8 }}>
          {disciplines.map(disc => {
            const s     = computeStats(byDiscipline[disc]);
            const color = DISCIPLINE_COLORS[disc]||"#888";
            const on    = disciplineFilter === disc;
            return (
              <div key={disc} onClick={() => setDisciplineFilter(on ? "all" : disc)} style={{
                background: on ? color+"12" : "#fff",
                border: `0.5px solid ${on ? color : "#e5e7eb"}`,
                borderLeft:`3px solid ${color}`,
                borderRadius:6, padding:"10px 12px", cursor:"pointer", transition:"all 0.15s",
              }}>
                <div style={{ fontSize:10, color, fontFamily:"monospace", fontWeight:600, marginBottom:4, letterSpacing:"0.03em" }}>{disc}</div>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"baseline" }}>
                  <span style={{ fontSize:18, color:"#1a1a18" }}>{s.mean?.toFixed(2)}</span>
                  <span style={{ fontSize:10, color:"#bbb", fontFamily:"monospace" }}>{s.passRate?.toFixed(0)}% · n={s.total}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ position:"relative", background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, padding:"1rem", marginBottom:"1.25rem" }}>
        <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:8 }}>pass rate vs. mean score</div>
        <svg ref={svgRef} viewBox={`0 0 ${SW} ${SH}`} style={{ width:"100%", display:"block" }} onMouseLeave={() => setTooltip(t=>({...t,visible:false}))}>
          <g transform={`translate(${SP.left},${SP.top})`}>
            {[20,40,60,80,100].map(t=><line key={t} x1={sx(t)} x2={sx(t)} y1={0} y2={scH} stroke="#f5f4f0" strokeWidth={0.5}/>)}
            {[1,2,3,4,5].map(t=><line key={t} x1={0} x2={scW} y1={sy(t)} y2={sy(t)} stroke="#f5f4f0" strokeWidth={0.5}/>)}
            <line x1={0} x2={scW} y1={sy(3)} y2={sy(3)} stroke="#e8e7e0" strokeWidth={1} strokeDasharray="4,3"/>
            <line x1={0} x2={0}   y1={0}    y2={scH}  stroke="#d4d3cc" strokeWidth={0.5}/>
            <line x1={0} x2={scW} y1={scH}  y2={scH}  stroke="#d4d3cc" strokeWidth={0.5}/>
            {[20,40,60,80,100].map(t=><text key={t} x={sx(t)} y={scH+14} textAnchor="middle" fontSize={9} fill="#ccc" fontFamily="monospace">{t}%</text>)}
            {[1,2,3,4,5].map(t=><text key={t} x={-6} y={sy(t)+3} textAnchor="end" fontSize={9} fill="#ccc" fontFamily="monospace">{t}</text>)}
            <text x={scW/2} y={scH+34} textAnchor="middle" fontSize={9} fill="#bbb" fontFamily="monospace" letterSpacing="0.05em">pass rate (%)</text>
            <text x={-scH/2} y={-34} textAnchor="middle" fontSize={9} fill="#bbb" fontFamily="monospace" letterSpacing="0.05em" transform="rotate(-90)">mean score</text>
            {scatter.map(r=>{
              const color=DISCIPLINE_COLORS[r.discipline]||"#888";
              const isHov=hoveredSubject===r.subject;
              return (
                <g key={r.subject} style={{cursor:"default"}}
                  onMouseMove={e=>{ setHoveredSubject(r.subject); handleScatterMove(e,r); }}
                  onMouseLeave={()=>{ setHoveredSubject(null); setTooltip(t=>({...t,visible:false})); }}>
                  <circle cx={sx(r.passRate)} cy={sy(r.mean)}
                    r={isHov ? 9 : Math.max(5, Math.sqrt(r.total)*1.3)}
                    fill={color} opacity={hoveredSubject&&!isHov?0.2:0.8}
                    stroke={isHov?"#1a1a18":"transparent"} strokeWidth={1.5}
                    style={{transition:"opacity 0.2s"}}/>
                </g>
              );
            })}
          </g>
        </svg>
        {tooltip.visible && tooltip.subject && (
          <div style={{ position:"absolute", left:tooltip.x+14, top:tooltip.y-10, background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, padding:"10px 14px", fontSize:12, pointerEvents:"none", zIndex:10, minWidth:180, boxShadow:"0 2px 8px rgba(0,0,0,0.08)", fontFamily:"monospace" }}>
            <div style={{ fontWeight:600, marginBottom:6, fontSize:13, fontFamily:"'Georgia',serif", color:"#1a1a18" }}>{tooltip.subject.subject}</div>
            <div style={{ color:"#888", marginBottom:2, fontSize:11 }}>Mean: <strong style={{color:"#1a1a18"}}>{tooltip.subject.mean?.toFixed(2)}</strong></div>
            <div style={{ color:"#888", marginBottom:2, fontSize:11 }}>Pass Rate: <strong style={{color:"#1a1a18"}}>{tooltip.subject.passRate?.toFixed(1)}%</strong></div>
            <div style={{ color:"#888", fontSize:11 }}>n = {tooltip.subject.total}</div>
          </div>
        )}
        <div style={{ display:"flex", flexWrap:"wrap", gap:10, marginTop:10 }}>
          {Object.entries(DISCIPLINE_COLORS).map(([d,c])=>(
            <span key={d} style={{ display:"flex", alignItems:"center", gap:5, fontSize:10, color:"#aaa", fontFamily:"monospace" }}>
              <span style={{ width:8, height:8, borderRadius:"50%", background:c, display:"inline-block" }}/>
              {d}
            </span>
          ))}
        </div>
      </div>

      <div style={{ background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, overflow:"hidden" }}>
        <div style={{ padding:"10px 16px", borderBottom:"0.5px solid #e8e7e0", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
          <span style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace" }}>
            {subjectRows.length} subjects {disciplineFilter!=="all"&&<span style={{color:DISCIPLINE_COLORS[disciplineFilter]}}> · {disciplineFilter}</span>}
          </span>
          <div style={{ display:"flex", gap:5 }}>
            {[["subject","A–Z"],["mean","Mean ↓"],["pass","Pass% ↓"],["total","n ↓"]].map(([k,l])=>(
              <button key={k} onClick={()=>setSortBy(k)} style={{
                fontSize:10, padding:"3px 8px", borderRadius:4, border:"0.5px solid",
                borderColor:sortBy===k?"#1a1a18":"#e5e7eb",
                background:sortBy===k?"#1a1a18":"transparent",
                color:sortBy===k?"#fff":"#bbb",
                cursor:"pointer", fontFamily:"monospace",
              }}>{l}</button>
            ))}
          </div>
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#FAFAF8" }}>
                {["Subject","Discipline","Distribution","Mean","Pass %","n"].map(h=>(
                  <th key={h} style={{ padding:"8px 14px", textAlign:["n","Mean","Pass %"].includes(h)?"center":"left", fontWeight:400, color:"#bbb", borderBottom:"0.5px solid #e8e7e0", fontFamily:"monospace", fontSize:10, letterSpacing:"0.06em", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subjectRows.map((r,i)=>{
                const color=DISCIPLINE_COLORS[r.discipline]||"#888";
                return (
                  <tr key={r.subject}
                    style={{ background:hoveredSubject===r.subject?"#f5f4f0":i%2===0?"#fff":"#FAFAF8", borderBottom:"0.5px solid #f0efe8", transition:"background 0.1s" }}
                    onMouseEnter={()=>setHoveredSubject(r.subject)} onMouseLeave={()=>setHoveredSubject(null)}>
                    <td style={{ padding:"9px 14px", color:"#1a1a18", fontWeight:500 }}>{r.subject}</td>
                    <td style={{ padding:"9px 14px" }}><span style={{ fontSize:10, color, fontFamily:"monospace", padding:"2px 6px", borderRadius:3, background:color+"12" }}>{r.discipline}</span></td>
                    <td style={{ padding:"9px 14px" }}><MiniBar dist={r.dist}/></td>
                    <td style={{ padding:"9px 14px", textAlign:"center", fontFamily:"monospace", color:"#444" }}>{r.mean?.toFixed(2)??"—"}</td>
                    <td style={{ padding:"9px 14px", textAlign:"center" }}><PassBadge rate={r.passRate}/></td>
                    <td style={{ padding:"9px 14px", textAlign:"center", fontFamily:"monospace", color:"#bbb", fontSize:11 }}>{r.total}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── Tab 2: Trends ────────────────────────────────────────────────────────────
function TrendsTab() {
  const { allData, loading } = useAllYearsData(true);
  const [metric, setMetric]               = useState("passRate");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [hoveredLine, setHoveredLine]     = useState(null);
  const [tooltip, setTooltip]             = useState({ visible:false, x:0, y:0, year:null, points:[] });
  const svgRef = useRef(null);

  const allSubjects = [...new Set(
    Object.values(allData).flatMap(rows => rows.map(r => normalizeSubject(r.subject)))
  )].sort();

  const seriesData = allSubjects.map(subject => {
    const points = ALL_YEARS.map(yr => {
      const rows = (allData[yr] || []).filter(r => normalizeSubject(r.subject) === subject);
      if (!rows.length) return { year: yr, value: null };
      const s = computeStats(rows);
      return { year: yr, value: metric === "passRate" ? s.passRate : metric === "mean" ? s.mean : s.total };
    }).filter(p => p.value !== null);
    const disc = Object.values(allData).flatMap(r => r).find(r => normalizeSubject(r.subject) === subject)?.discipline || "";
    return { subject, discipline: disc, points };
  }).filter(s => s.points.length >= 2);

  const displayed = selectedSubjects.length>0 ? seriesData.filter(s=>selectedSubjects.includes(s.subject)) : seriesData;

  const W=700,H=340,PAD={top:20,right:20,bottom:44,left:50};
  const cW=W-PAD.left-PAD.right, cH=H-PAD.top-PAD.bottom;
  const xPos = ALL_YEARS.reduce((acc,yr,i)=>{ acc[yr]=(i/(ALL_YEARS.length-1))*cW; return acc; },{});
  const allVals = displayed.flatMap(s=>s.points.map(p=>p.value)).filter(Boolean);
  const yMin = allVals.length ? Math.max(0, Math.floor(Math.min(...allVals)*0.93)) : 0;
  const yMax = allVals.length ? Math.ceil(Math.max(...allVals)*1.04) : 100;
  function yScale(v){ return cH-((v-yMin)/(yMax-yMin))*cH; }
  const yTicks = Array.from({length:5},(_,i)=>yMin+((yMax-yMin)/4)*i);

  function fmt(v){
    if (metric==="passRate") return v?.toFixed(1)+"%";
    if (metric==="mean")     return v?.toFixed(2);
    return Math.round(v).toString();
  }

  function handleMove(e){
    if (!svgRef.current) return;
    const rect=svgRef.current.getBoundingClientRect();
    const mx=e.clientX-rect.left-PAD.left;
    const nearestYr=ALL_YEARS.reduce((b,yr)=>Math.abs(xPos[yr]-mx)<Math.abs(xPos[b]-mx)?yr:b,ALL_YEARS[0]);
    const pts=displayed.map(s=>({...s,pt:s.points.find(p=>p.year===nearestYr)})).filter(s=>s.pt).sort((a,b)=>(b.pt.value||0)-(a.pt.value||0));
    setTooltip({ visible:true, x:e.clientX-rect.left, y:e.clientY-rect.top, year:nearestYr, points:pts.slice(0,10) });
  }

  function toggle(subj){ setSelectedSubjects(p=>p.includes(subj)?p.filter(s=>s!==subj):[...p,subj]); }

  if (loading) return <LoadingState />;

  return (
    <>
      {/* ── Aggregate section ── */}
      <AggregateTrendsChart allData={allData} />

      {/* ── Divider ── */}
      <div style={{ borderTop:"1px solid #e8e7e0", marginBottom:"1.75rem", paddingTop:"1.75rem" }}>
        <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:"1.25rem" }}>
          by subject · drill down
        </div>
      </div>

      {/* ── Subject controls ── */}
      <div style={{ display:"flex", gap:20, marginBottom:"1.25rem", flexWrap:"wrap", alignItems:"flex-start" }}>
        <div>
          <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:6 }}>metric</div>
          <div style={{ display:"flex", gap:6 }}>
            {[["passRate","Pass Rate"],["mean","Mean Score"],["total","Volume"]].map(([k,l])=>(
              <button key={k} onClick={()=>setMetric(k)} style={{
                fontSize:11, padding:"5px 12px", borderRadius:6, border:"0.5px solid",
                borderColor:metric===k?"#1a1a18":"#d4d3cc", background:metric===k?"#1a1a18":"transparent",
                color:metric===k?"#fff":"#666", cursor:"pointer", fontFamily:"monospace", transition:"all 0.15s",
              }}>{l}</button>
            ))}
          </div>
        </div>
        <div style={{ flex:1, minWidth:280 }}>
          <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:6 }}>
            highlight subjects {selectedSubjects.length>0&&<span style={{color:"#1a1a18"}}>· {selectedSubjects.length} selected</span>}
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:5, maxHeight:96, overflowY:"auto" }}>
            {seriesData.map(s=>{
              const on=selectedSubjects.includes(s.subject);
              const c=DISCIPLINE_COLORS[s.discipline]||"#888";
              return <button key={s.subject} onClick={()=>toggle(s.subject)} style={{ fontSize:10, padding:"3px 8px", borderRadius:4, border:"0.5px solid", borderColor:on?c:"#e5e7eb", background:on?c+"15":"transparent", color:on?c:"#bbb", cursor:"pointer", fontFamily:"monospace", transition:"all 0.15s" }}>{s.subject}</button>;
            })}
          </div>
          {selectedSubjects.length>0&&<button onClick={()=>setSelectedSubjects([])} style={{ fontSize:10, color:"#bbb", fontFamily:"monospace", border:"none", background:"none", cursor:"pointer", marginTop:4, padding:0 }}>clear</button>}
        </div>
      </div>

      {/* Subject line chart */}
      <div style={{ position:"relative", background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, padding:"1rem", marginBottom:"1.25rem" }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", display:"block", cursor:"crosshair" }}
          onMouseMove={handleMove} onMouseLeave={()=>setTooltip(t=>({...t,visible:false}))}>
          <g transform={`translate(${PAD.left},${PAD.top})`}>
            {yTicks.map(t=><line key={t} x1={0} x2={cW} y1={yScale(t)} y2={yScale(t)} stroke="#f5f4f0" strokeWidth={0.5}/>)}
            {ALL_YEARS.map(yr=><line key={yr} x1={xPos[yr]} x2={xPos[yr]} y1={0} y2={cH} stroke="#f5f4f0" strokeWidth={0.5}/>)}
            <line x1={0} x2={0}  y1={0}  y2={cH}  stroke="#d4d3cc" strokeWidth={0.5}/>
            <line x1={0} x2={cW} y1={cH} y2={cH} stroke="#d4d3cc" strokeWidth={0.5}/>
            {ALL_YEARS.map(yr=><text key={yr} x={xPos[yr]} y={cH+15} textAnchor="middle" fontSize={9} fill="#ccc" fontFamily="monospace">{yr}</text>)}
            {yTicks.map(t=><text key={t} x={-6} y={yScale(t)+3} textAnchor="end" fontSize={9} fill="#ccc" fontFamily="monospace">{fmt(t)}</text>)}
            {displayed.map(s=>{
              const color=DISCIPLINE_COLORS[s.discipline]||"#888";
              const isHov=hoveredLine===s.subject;
              const isSel=selectedSubjects.includes(s.subject);
              const opacity=selectedSubjects.length===0?0.55:isSel?1:0.07;
              const pathD=s.points.filter(p=>p.value!==null).map((p,i)=>`${i===0?"M":"L"} ${xPos[p.year]} ${yScale(p.value)}`).join(" ");
              return (
                <g key={s.subject} onMouseEnter={()=>setHoveredLine(s.subject)} onMouseLeave={()=>setHoveredLine(null)} onClick={()=>toggle(s.subject)} style={{cursor:"pointer"}}>
                  <path d={pathD} fill="none" stroke={color} strokeWidth={isHov||isSel?2.5:1} opacity={isHov?1:opacity} style={{transition:"opacity 0.2s, stroke-width 0.15s"}}/>
                  {s.points.filter(p=>p.value!==null).map(p=>(
                    <circle key={p.year} cx={xPos[p.year]} cy={yScale(p.value)} r={isHov||isSel?4:2.5} fill={color} opacity={isHov?1:opacity} style={{transition:"opacity 0.2s"}}/>
                  ))}
                  {(isSel||isHov)&&(()=>{
                    const last=s.points[s.points.length-1]; if(!last)return null;
                    return <text x={xPos[last.year]+7} y={yScale(last.value)+3} fontSize={9} fill={color} fontFamily="monospace" fontWeight={600}>{s.subject.length>18?s.subject.slice(0,16)+"…":s.subject}</text>;
                  })()}
                </g>
              );
            })}
          </g>
        </svg>
        {tooltip.visible&&tooltip.points.length>0&&(
          <div style={{ position:"absolute", left:Math.min(tooltip.x+14,480), top:Math.max(tooltip.y-10,0), background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, padding:"10px 14px", fontSize:11, pointerEvents:"none", zIndex:10, minWidth:200, maxWidth:260, boxShadow:"0 2px 12px rgba(0,0,0,0.08)", fontFamily:"monospace" }}>
            <div style={{ fontWeight:600, marginBottom:8, fontSize:13, color:"#1a1a18", fontFamily:"'Georgia',serif" }}>{tooltip.year}</div>
            {tooltip.points.map(s=>(
              <div key={s.subject} style={{ display:"flex", justifyContent:"space-between", gap:12, marginBottom:3 }}>
                <span style={{ color:DISCIPLINE_COLORS[s.discipline]||"#888", fontSize:10 }}>{s.subject}</span>
                <span style={{ color:"#1a1a18", fontWeight:600 }}>{fmt(s.pt.value)}</span>
              </div>
            ))}
            {displayed.length>10&&<div style={{color:"#ddd",fontSize:9,marginTop:4}}>+ {displayed.length-10} more</div>}
          </div>
        )}
      </div>

      {selectedSubjects.length>0&&(
        <div style={{ background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, overflow:"hidden" }}>
          <div style={{ padding:"10px 16px", borderBottom:"0.5px solid #e8e7e0", fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace" }}>year-over-year · selected subjects</div>
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:11 }}>
              <thead>
                <tr style={{ background:"#FAFAF8" }}>
                  <th style={{ padding:"7px 14px", textAlign:"left", fontWeight:400, color:"#bbb", borderBottom:"0.5px solid #e8e7e0", fontFamily:"monospace", fontSize:10 }}>Subject</th>
                  {ALL_YEARS.map(yr=><th key={yr} style={{ padding:"7px 10px", textAlign:"center", fontWeight:400, color:"#bbb", borderBottom:"0.5px solid #e8e7e0", fontFamily:"monospace", fontSize:10 }}>{yr}</th>)}
                </tr>
              </thead>
              <tbody>
                {seriesData.filter(s=>selectedSubjects.includes(s.subject)).map((s,i)=>{
                  const color=DISCIPLINE_COLORS[s.discipline]||"#888";
                  const ptMap=Object.fromEntries(s.points.map(p=>[p.year,p.value]));
                  return (
                    <tr key={s.subject} style={{ background:i%2===0?"#fff":"#FAFAF8", borderBottom:"0.5px solid #f0efe8" }}>
                      <td style={{ padding:"8px 14px", color, fontWeight:600, fontFamily:"monospace", fontSize:11 }}>{s.subject}</td>
                      {ALL_YEARS.map(yr=>{
                        const v=ptMap[yr];
                        const prev=ptMap[ALL_YEARS[ALL_YEARS.indexOf(yr)-1]];
                        const delta=v!=null&&prev!=null?v-prev:null;
                        return (
                          <td key={yr} style={{ padding:"8px 10px", textAlign:"center", fontFamily:"monospace", color:v!=null?"#444":"#ddd", fontSize:11 }}>
                            {v!=null?(
                              <div>
                                <div>{fmt(v)}</div>
                                {delta!=null&&<div style={{ fontSize:9, color:delta>0?"#1D9E75":delta<0?"#E24B4A":"#bbb" }}>{delta>0?"+":""}{fmt(delta)}</div>}
                              </div>
                            ):"—"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop:"1rem", fontSize:10, color:"#ccc", fontFamily:"monospace", letterSpacing:"0.04em" }}>
        Click subject tags or lines to highlight. Hover chart for year values. Delta shown below each value.
      </div>
    </>
  );
}

// ─── Tab 3: Score by Grade ────────────────────────────────────────────────────
function ByGradeTab({ data, loading, error, year }) {
  const [gradeFilter, setGradeFilter]         = useState("all");
  const [selectedSubject, setSelectedSubject] = useState(null);

  if (loading) return <LoadingState />;
  if (error)   return <div style={{ padding:"3rem", color:"#E24B4A", fontFamily:"monospace", fontSize:12 }}>Error: {error}</div>;
  if (!data.length) return <EmptyState year={year} />;

  const normalizedData = data.map(r => ({ ...r, subject: normalizeSubject(r.subject) }));
  const grades        = ["10th Grade","11th Grade","12th Grade"];
  const visibleGrades = gradeFilter==="all" ? grades : [gradeFilter];
  const bySubject     = groupBy(normalizedData, "subject");
  const byGrade       = groupBy(normalizedData, "gradeLevel");

  const subjectRows = Object.entries(bySubject).map(([subject,allRows])=>{
    const byG={};
    grades.forEach(g=>{ byG[g]=computeStats((byGrade[g]||[]).filter(r=>r.subject===subject)); });
    return { subject, byG };
  }).filter(r=>visibleGrades.some(g=>r.byG[g].total>0));

  const displayed = selectedSubject ? subjectRows.filter(r=>r.subject===selectedSubject) : subjectRows;

  return (
    <>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:"1.5rem" }}>
        {grades.map(g=>{
          const s=computeStats(byGrade[g]||[]);
          return <StatCard key={g} label={g} value={s.mean?.toFixed(2)??"—"} sub={`${s.passRate?.toFixed(1)??"—"}% passing · n=${s.total}`} accent={GRADE_COLORS[g]}/>;
        })}
      </div>

      <div style={{ display:"flex", gap:16, marginBottom:"1.25rem", flexWrap:"wrap", alignItems:"flex-end" }}>
        <div>
          <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:6 }}>grade</div>
          <div style={{ display:"flex", gap:6 }}>
            {["all",...grades].map(g=>(
              <button key={g} onClick={()=>setGradeFilter(g)} style={{
                fontSize:11, padding:"5px 12px", borderRadius:6, border:"0.5px solid",
                borderColor:gradeFilter===g?"#1a1a18":"#d4d3cc", background:gradeFilter===g?"#1a1a18":"transparent",
                color:gradeFilter===g?"#fff":"#666", cursor:"pointer", fontFamily:"monospace", transition:"all 0.15s",
              }}>{g==="all"?"All":g.replace("th Grade","")}</button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:6 }}>subject</div>
          <select value={selectedSubject||""} onChange={e=>setSelectedSubject(e.target.value||null)}
            style={{ fontSize:12, padding:"5px 10px", borderRadius:6, border:"0.5px solid #d4d3cc", background:"#fff", color:"#1a1a18", fontFamily:"monospace" }}>
            <option value="">All subjects</option>
            {Object.keys(bySubject).sort().map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div style={{ background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, overflow:"hidden", marginBottom:"1.25rem" }}>
        <div style={{ padding:"10px 16px", borderBottom:"0.5px solid #e8e7e0", fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace" }}>
          score by grade · {year} · {displayed.length} subjects
        </div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:"#FAFAF8" }}>
                <th style={{ padding:"8px 14px", textAlign:"left", fontWeight:400, color:"#bbb", borderBottom:"0.5px solid #e8e7e0", fontFamily:"monospace", fontSize:10 }}>Subject</th>
                {visibleGrades.flatMap(g=>
                  ["Dist.","Mean","Pass%","n"].map(col=>(
                    <th key={`${g}-${col}`} style={{ padding:"8px 10px", textAlign:"center", fontWeight:400, borderBottom:"0.5px solid #e8e7e0", fontFamily:"monospace", fontSize:10, color:GRADE_COLORS[g], whiteSpace:"nowrap" }}>
                      {g.replace("th Grade","")} {col}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {displayed.map((r,i)=>(
                <tr key={r.subject} style={{ background:i%2===0?"#fff":"#FAFAF8", borderBottom:"0.5px solid #f0efe8" }}>
                  <td style={{ padding:"9px 14px", color:"#1a1a18", fontWeight:500, whiteSpace:"nowrap" }}>{r.subject}</td>
                  {visibleGrades.flatMap(g=>{
                    const s=r.byG[g];
                    return [
                      <td key={`${g}-d`} style={{padding:"9px 10px"}}>{s.total>0?<MiniBar dist={s.dist}/>:<span style={{color:"#ddd",fontFamily:"monospace",fontSize:10}}>—</span>}</td>,
                      <td key={`${g}-m`} style={{padding:"9px 10px",textAlign:"center",fontFamily:"monospace",color:s.mean?GRADE_COLORS[g]:"#ddd",fontWeight:s.mean?600:400,fontSize:11}}>{s.mean?.toFixed(2)??"—"}</td>,
                      <td key={`${g}-p`} style={{padding:"9px 10px",textAlign:"center"}}><PassBadge rate={s.passRate}/></td>,
                      <td key={`${g}-n`} style={{padding:"9px 10px",textAlign:"center",fontFamily:"monospace",color:"#bbb",fontSize:10}}>{s.total||"—"}</td>,
                    ];
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSubject&&(()=>{
        const r=subjectRows.find(x=>x.subject===selectedSubject); if(!r) return null;
        return (
          <div style={{ background:"#fff", border:"0.5px solid #d4d3cc", borderRadius:8, padding:"1.25rem" }}>
            <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:"1rem" }}>{selectedSubject} · score counts by grade</div>
            <div style={{ display:"flex", gap:40, flexWrap:"wrap" }}>
              {visibleGrades.map(g=>{
                const s=r.byG[g];
                const max=Math.max(...[1,2,3,4,5].map(sc=>s.dist[sc]||0),1);
                return (
                  <div key={g}>
                    <div style={{ fontSize:11, color:GRADE_COLORS[g], fontFamily:"monospace", marginBottom:10, fontWeight:600 }}>{g} {s.total>0?`(n=${s.total})`:"(no data)"}</div>
                    <div style={{ display:"flex", gap:6, alignItems:"flex-end", height:90 }}>
                      {[1,2,3,4,5].map(sc=>{
                        const count=s.dist[sc]||0;
                        const h=s.total>0?Math.max((count/max)*75,count>0?4:0):0;
                        return (
                          <div key={sc} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                            <span style={{ fontSize:10, fontFamily:"monospace", color:count>0?"#666":"#ddd" }}>{count>0?count:""}</span>
                            <div style={{ width:30, height:h, background:SCORE_COLORS[sc], borderRadius:"2px 2px 0 0", transition:"height 0.3s" }}/>
                            <span style={{ fontSize:10, fontFamily:"monospace", color:"#bbb" }}>{sc}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function APScoreAlignmentExplorer() {
  const [tab, setTab]   = useState("overview");
  const [year, setYear] = useState("2026");
  const { data, loading, error } = useAPData(year);
  const showYearPicker = tab !== "trends";

  return (
    <div style={{ fontFamily:"'Georgia','Times New Roman',serif", background:"#FAFAF8", minHeight:"100vh", padding:"2rem", color:"#1a1a18" }}>
      <div style={{ maxWidth:940, margin:"0 auto" }}>

        <div style={{ marginBottom:"1.75rem", borderBottom:"1.5px solid #1a1a18", paddingBottom:"1rem" }}>
          <div style={{ fontSize:10, letterSpacing:"0.14em", textTransform:"uppercase", color:"#bbb", marginBottom:6, fontFamily:"monospace" }}>
            King's Academy · AP Program Analysis · Madaba, Jordan
          </div>
          <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
            <div>
              <h1 style={{ fontSize:26, fontWeight:400, margin:0, letterSpacing:"-0.02em" }}>AP Score Explorer</h1>
              <p style={{ fontSize:13, color:"#888", margin:"4px 0 0", lineHeight:1.5 }}>
                Exam performance · {ALL_YEARS[0]}–{ALL_YEARS[ALL_YEARS.length-1]}
              </p>
            </div>
            {showYearPicker&&(
              <div>
                <div style={{ fontSize:10, color:"#bbb", letterSpacing:"0.1em", textTransform:"uppercase", fontFamily:"monospace", marginBottom:6 }}>exam year</div>
                <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                  {ALL_YEARS.map(y=><YearPill key={y} value={y} active={year===y} onClick={()=>setYear(y)}/>)}
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display:"flex", marginBottom:"1.75rem", borderBottom:"0.5px solid #d4d3cc" }}>
          {TABS.map(t=>(
            <button key={t.key} onClick={()=>setTab(t.key)} style={{
              fontSize:11, padding:"8px 20px", border:"none",
              borderBottom:tab===t.key?"2px solid #1a1a18":"2px solid transparent",
              background:"transparent", color:tab===t.key?"#1a1a18":"#bbb",
              cursor:"pointer", fontFamily:"monospace", letterSpacing:"0.06em",
              textTransform:"uppercase", transition:"all 0.15s", marginBottom:-1,
            }}>{t.label}</button>
          ))}
        </div>

        {tab==="overview" && <OverviewTab data={data} loading={loading} error={error} year={year}/>}
        {tab==="trends"   && <TrendsTab/>}
        {tab==="bygrade"  && <ByGradeTab data={data} loading={loading} error={error} year={year}/>}

      </div>
    </div>
  );
}